import { createContext, useCallback, useContext, useEffect, useState } from "react";
import { applyBrandName } from "@/lib/constants";
import { api } from "@/lib/api";

// Opções de layout/skin do AdminLTE 3 (as mesmas do painel "Customize AdminLTE" do demo oficial)
const ThemeContext = createContext(null);
const KEY = "zitec_skin";

export const DEFAULT_SKIN = {
  brandName: "Zitec Atendimento",
  brandLogo: "",
  brandLogoCircle: true,
  brandShowTitle: true,
  darkMode: false,
  navbarVariant: "navbar-white navbar-light",
  sidebarVariant: "sidebar-dark-primary",
  accent: "",
  brandVariant: "",
  navbarFixed: false,
  footerFixed: false,
  sidebarCollapsed: false,
  textSm: false,
  navFlat: false,
  navLegacy: false,
  navCompact: false,
  navChildIndent: false,
};

const SKIN_COLORS = ["primary", "warning", "info", "danger", "success", "indigo", "lightblue", "navy", "purple", "fuchsia", "pink", "maroon", "orange", "lime", "teal", "olive"];
const NAVBAR_DARK = ["primary", "secondary", "info", "success", "danger", "indigo", "purple", "pink", "navy", "lightblue", "teal", "cyan", "dark", "gray-dark", "gray"];
const NAVBAR_LIGHT = ["light", "warning", "white", "orange"];

export const NAVBAR_VARIANTS = [
  ...NAVBAR_DARK.map((c) => ({ value: `navbar-${c} navbar-dark`, label: c })),
  ...NAVBAR_LIGHT.map((c) => ({ value: `navbar-${c} navbar-light`, label: c })),
];
export const SIDEBAR_VARIANTS = [
  ...SKIN_COLORS.map((c) => ({ value: `sidebar-dark-${c}`, label: `dark-${c}` })),
  ...SKIN_COLORS.map((c) => ({ value: `sidebar-light-${c}`, label: `light-${c}` })),
];
export const ACCENT_VARIANTS = SKIN_COLORS.map((c) => ({ value: `accent-${c}`, label: c }));
export const BRAND_VARIANTS = [...NAVBAR_DARK, ...NAVBAR_LIGHT].map((c) => ({ value: `navbar-${c}`, label: c }));

// Tema global (banco, GET/PUT /api/settings/theme): tudo menos o modo noturno, que é preferência de cada pessoa.
// O localStorage guarda a última versão vista, só para a tela já abrir com a marca certa enquanto a API responde
const GLOBAL_KEYS = Object.keys(DEFAULT_SKIN).filter((k) => k !== "darkMode");
export const globalPart = (s) => Object.fromEntries(GLOBAL_KEYS.map((k) => [k, s[k]]));
const fromServer = (data) => Object.fromEntries(GLOBAL_KEYS.filter((k) => data?.[k] !== undefined).map((k) => [k, data[k]]));

const loadSkin = () => {
  try {
    return { ...DEFAULT_SKIN, ...JSON.parse(localStorage.getItem(KEY) || "{}") };
  } catch {
    return DEFAULT_SKIN;
  }
};

export const ThemeProvider = ({ children }) => {
  const [skin, setSkin] = useState(loadSkin);
  // loaded: já veio da API; empty: o gestor ainda não salvou nenhum tema no banco
  const [remote, setRemote] = useState({ loaded: false, empty: false });
  // Aplicado já no render para os rótulos de etapa saírem com o nome atual da marca
  applyBrandName(skin.brandName);

  useEffect(() => {
    document.body.classList.toggle("dark-mode", skin.darkMode);
    try { localStorage.setItem(KEY, JSON.stringify(skin)); } catch { /* storage indisponível */ }
  }, [skin]);

  // Tema do banco vale para todos: substitui o que estava no navegador (menos o modo noturno)
  useEffect(() => {
    let vivo = true;
    api.get("/settings/theme")
      .then(({ data }) => {
        if (!vivo) return;
        const tema = fromServer(data);
        const empty = Object.keys(tema).length === 0;
        if (!empty) setSkin((s) => ({ ...s, ...globalPart(DEFAULT_SKIN), ...tema }));
        setRemote({ loaded: true, empty });
      })
      .catch(() => { if (vivo) setRemote({ loaded: true, empty: false, error: true }); });
    return () => { vivo = false; };
  }, []);

  // Só o gestor chega aqui (tela de Customização); a API também confere o nível
  const saveGlobal = useCallback(async (s) => {
    await api.put("/settings/theme", globalPart(s));
    setRemote((r) => ({ ...r, empty: false }));
  }, []);

  const update = (patch) => setSkin((s) => ({ ...s, ...patch }));
  // Restaura o layout/cores do AdminLTE sem apagar a marca (nome e logo)
  const reset = () => setSkin((s) => ({ ...DEFAULT_SKIN, brandName: s.brandName, brandLogo: s.brandLogo, brandLogoCircle: s.brandLogoCircle, brandShowTitle: s.brandShowTitle }));
  const toggleDarkMode = () => setSkin((s) => ({ ...s, darkMode: !s.darkMode }));

  // No modo noturno o navbar padrão (branco) passa para navbar-dark, como nas páginas dark do AdminLTE
  const navbarClass = skin.darkMode && skin.navbarVariant === DEFAULT_SKIN.navbarVariant ? "navbar-dark" : skin.navbarVariant;

  return (
    <ThemeContext.Provider value={{ skin, update, reset, toggleDarkMode, navbarClass, remote, saveGlobal }}>
      {children}
    </ThemeContext.Provider>
  );
};

export const useTheme = () => useContext(ThemeContext);
